import { useState, useRef, useEffect } from 'react';
import * as tf from '@tensorflow/tfjs';
import { useAppStore } from '../../store/appStore';
import { Landmark, CustomGesture } from '../../types';
import { modelTrainingService } from '../../services/model-training.service';
import { apiService } from '../../services/api.service';
import './CustomGestureTrainer.css';

export const CustomGestureTrainer = () => {
  const { 
    customGestures, 
    addCustomGesture, 
    removeCustomGesture, 
    updateCustomGesture,
    setTrainingMode, 
    isTrainingMode,
    isAuthenticated 
  } = useAppStore();
  
  const [gestureName, setGestureName] = useState('');
  const [gestureDescription, setGestureDescription] = useState('');
  const [trainingData, setTrainingData] = useState<Landmark[][]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingCount, setRecordingCount] = useState(0);
  const [isTraining, setIsTraining] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState({ epoch: 0, loss: 0, accuracy: 0 });
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const landmarkCaptureRef = useRef<((landmarks: Landmark[]) => void) | null>(null);

  useEffect(() => {
    // Load pretrained model on mount
    modelTrainingService.loadPretrainedModel();

    // Listen for landmarks from camera
    const handleLandmarksCaptured = (event: any) => {
      if (isRecording && isTrainingMode) {
        const { landmarks } = event.detail;
        captureLandmark(landmarks);
      }
    };

    window.addEventListener('landmarksCaptured', handleLandmarksCaptured);

    return () => {
      window.removeEventListener('landmarksCaptured', handleLandmarksCaptured);
    };
  }, [isRecording, isTrainingMode]);

  const startTraining = () => {
    if (!gestureName.trim()) {
      alert('Please enter a gesture name');
      return;
    }
    setTrainingMode(true);
    setTrainingData([]);
    setRecordingCount(0);
    setIsRecording(true);
  };

  const captureLandmark = (landmarks: Landmark[]) => {
    if (!isRecording || !isTrainingMode) return;
    
    setTrainingData(prev => {
      const updated = [...prev, landmarks];
      setRecordingCount(updated.length);
      
      // Auto-stop at 30 samples
      if (updated.length >= 30) {
        setIsRecording(false);
      }
      
      return updated;
    });
  };

  // Expose capture function to parent
  useEffect(() => {
    landmarkCaptureRef.current = captureLandmark;
  }, [isRecording, isTrainingMode]);

  const trainModel = async () => {
    if (trainingData.length < 10) {
      alert('Please record at least 10 samples');
      return;
    }

    setIsTraining(true);

    try {
      const gesture: CustomGesture = {
        id: `custom_${Date.now()}`,
        name: gestureName,
        description: gestureDescription,
        trainingData: trainingData,
        accuracy: 0,
        createdAt: Date.now()
      };

      // Train the model
      const model = await modelTrainingService.trainCustomGesture(
        gesture,
        (epoch, loss, accuracy) => {
          setTrainingProgress({ epoch, loss, accuracy });
        }
      );

      // Save model to IndexedDB
      await modelTrainingService.saveModel(model, gesture.id);

      // Calculate final accuracy
      const accuracy = await evaluateModel(model, trainingData);
      gesture.accuracy = accuracy;

      // Save gesture
      addCustomGesture(gesture);

      // Sync to cloud if authenticated
      if (isAuthenticated) {
        try {
          await apiService.createGesture({
            name: gesture.name,
            description: gesture.description,
            landmarks: gesture.trainingData
          });
        } catch (error) {
          console.error('Failed to sync to cloud:', error);
        }
      }

      // Reset form
      setGestureName('');
      setGestureDescription('');
      setTrainingData([]);
      setRecordingCount(0);
      setTrainingMode(false);
      setIsTraining(false);
      setTrainingProgress({ epoch: 0, loss: 0, accuracy: 0 });

      alert(`Gesture "${gesture.name}" trained successfully!\nAccuracy: ${(accuracy * 100).toFixed(1)}%`);
    } catch (error) {
      console.error('Training failed:', error);
      alert('Training failed. Please try again.');
      setIsTraining(false);
    }
  };

  const evaluateModel = async (
    model: tf.LayersModel,
    samples: Landmark[][]
  ): Promise<number> => {
    let correct = 0;
    
    for (const sample of samples) {
      const confidence = await modelTrainingService.predictWithModel(model, sample);
      if (confidence > 0.5) correct++;
    }
    
    return correct / samples.length;
  };

  const cancelTraining = () => {
    setTrainingMode(false);
    setTrainingData([]);
    setRecordingCount(0);
    setIsRecording(false);
    setIsTraining(false);
  };

  const deleteGesture = async (gesture: CustomGesture) => {
    if (!confirm(`Are you sure you want to delete "${gesture.name}"?`)) return;

    // Delete model from IndexedDB
    await modelTrainingService.deleteModel(gesture.id);

    // Delete from cloud if authenticated
    if (isAuthenticated) {
      try {
        await apiService.deleteGesture(gesture.id);
      } catch (error) {
        console.error('Failed to delete from cloud:', error);
      }
    }

    removeCustomGesture(gesture.id);
  };

  const retrainGesture = async (gesture: CustomGesture) => {
    if (!confirm(`Retrain "${gesture.name}" with current samples?`)) return;

    setIsTraining(true);

    try {
      const model = await modelTrainingService.trainCustomGesture(
        gesture,
        (epoch, loss, accuracy) => {
          setTrainingProgress({ epoch, loss, accuracy });
        }
      );

      await modelTrainingService.saveModel(model, gesture.id);
      const accuracy = await evaluateModel(model, gesture.trainingData);

      updateCustomGesture(gesture.id, { accuracy });

      alert(`Gesture retrained!\nNew accuracy: ${(accuracy * 100).toFixed(1)}%`);
    } catch (error) {
      console.error('Retraining failed:', error);
      alert('Retraining failed. Please try again.');
    } finally {
      setIsTraining(false);
      setTrainingProgress({ epoch: 0, loss: 0, accuracy: 0 });
    }
  };

  return (
    <div className="custom-gesture-trainer">
      <h2>Custom Gesture Training</h2>
      <p className="trainer-description">
        Train your own custom gestures using transfer learning and TensorFlow.js
      </p>
      
      {!isTrainingMode ? (
        <div className="training-form">
          <div className="form-group">
            <label>Gesture Name:</label>
            <input
              type="text"
              value={gestureName}
              onChange={(e) => setGestureName(e.target.value)}
              placeholder="e.g., Hello, Goodbye, Help"
            />
          </div>
          
          <div className="form-group">
            <label>Description (optional):</label>
            <textarea
              value={gestureDescription}
              onChange={(e) => setGestureDescription(e.target.value)}
              placeholder="Describe the gesture..."
              rows={3}
            />
          </div>
          
          <button onClick={startTraining} className="btn btn-primary">
            Start Training
          </button>
        </div>
      ) : (
        <div className="training-session">
          <h3>Training: {gestureName}</h3>
          
          {!isTraining ? (
            <>
              <div className="recording-status">
                <p className="sample-count">
                  Samples recorded: {recordingCount} / 30 (minimum 10)
                </p>
                {isRecording && (
                  <div className="recording-indicator">
                    <span className="recording-dot"></span>
                    Recording... Perform the gesture now!
                  </div>
                )}
              </div>
              
              <div className="training-instructions">
                <h4>Instructions:</h4>
                <ul>
                  <li>Perform the gesture in front of the camera</li>
                  <li>Hold each pose for 1-2 seconds</li>
                  <li>Try different angles and distances</li>
                  <li>Vary hand positions slightly for better accuracy</li>
                </ul>
              </div>
              
              <div className="training-actions">
                <button 
                  onClick={() => setIsRecording(!isRecording)} 
                  className={`btn ${isRecording ? 'btn-warning' : 'btn-primary'}`}
                >
                  {isRecording ? 'Pause Recording' : 'Resume Recording'}
                </button>
                <button 
                  onClick={trainModel} 
                  className="btn btn-success"
                  disabled={recordingCount < 10}
                >
                  Train Model ({recordingCount} samples)
                </button>
                <button onClick={cancelTraining} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
              
              <div className="training-progress">
                <div 
                  className="progress-bar" 
                  style={{ width: `${(recordingCount / 30) * 100}%` }}
                />
              </div>
            </>
          ) : (
            <div className="training-in-progress">
              <h4>Training Model...</h4>
              <p>Epoch: {trainingProgress.epoch + 1} / 50</p>
              <p>Loss: {trainingProgress.loss.toFixed(4)}</p>
              <p>Accuracy: {(trainingProgress.accuracy * 100).toFixed(1)}%</p>
              <div className="training-progress">
                <div 
                  className="progress-bar training" 
                  style={{ width: `${((trainingProgress.epoch + 1) / 50) * 100}%` }}
                />
              </div>
              <p className="training-note">This may take 1-2 minutes...</p>
            </div>
          )}
        </div>
      )}
      
      <div className="custom-gestures-list">
        <h3>Your Custom Gestures ({customGestures.length})</h3>
        {customGestures.length === 0 ? (
          <p className="empty-state">No custom gestures yet. Create one above!</p>
        ) : (
          <div className="gestures-grid">
            {customGestures.map(gesture => (
              <div key={gesture.id} className="gesture-card">
                <h4>{gesture.name}</h4>
                {gesture.description && <p className="gesture-desc">{gesture.description}</p>}
                <div className="gesture-stats">
                  <span>Samples: {gesture.trainingData.length}</span>
                  <span>Accuracy: {((gesture.accuracy || 0) * 100).toFixed(1)}%</span>
                </div>
                <div className="gesture-actions">
                  <button 
                    onClick={() => retrainGesture(gesture)}
                    className="btn btn-primary btn-sm"
                    disabled={isTraining}
                  >
                    Retrain
                  </button>
                  <button 
                    onClick={() => deleteGesture(gesture)}
                    className="btn btn-danger btn-sm"
                    disabled={isTraining}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
